/**
 * SESSION HANDSHAKE — condivisione della sessione tra tab via BroadcastChannel.
 *
 * La sessione utente vive in sessionStorage (refreshToken/webId/apiHost) + memoria
 * (accessToken), quindi NON è condivisa tra tab (scelta di sicurezza, vedi
 * tokenManager.ts / AUTH_LOGIN_GUIDE.md). Aprendo un link esterno (es. la mail di
 * avviso menzione) in una nuova tab, questa non avrebbe sessione e finirebbe al login
 * pur essendo l'utente già autenticato altrove.
 *
 * Rimedio: una tab senza sessione, all'avvio, CHIEDE via BroadcastChannel a una tab
 * già autenticata di passarle il refreshToken; se qualcuno risponde entra senza login.
 * Il token NON viene mai persistito su disco: resta in sessionStorage/memoria, solo
 * propagato tra tab vive. Se nessun'altra tab è aperta, si ricade sul login normale.
 */

import { getApiHost, getRefreshToken, getWebId } from './tokenManager';

const CHANNEL_NAME = 'crm-auth-session';

interface SessionPayload {
  refreshToken: string;
  webId: string;
  apiHost: string | null;
}

interface SessionRequest {
  type: 'session-request';
  nonce: string;
}

interface SessionResponse extends SessionPayload {
  type: 'session-response';
  nonce: string;
}

type ChannelMessage = SessionRequest | SessionResponse;

/**
 * Avvia il "responder": questa tab, se possiede una sessione, risponde alle richieste
 * delle altre tab. Da chiamare una volta all'avvio (è innocuo nelle tab senza sessione:
 * semplicemente non risponde). Il canale resta aperto per tutta la vita della tab.
 */
export function startSessionResponder(): void {
  if (typeof BroadcastChannel === 'undefined') return;

  const channel = new BroadcastChannel(CHANNEL_NAME);

  channel.onmessage = (event: MessageEvent<ChannelMessage>) => {
    const message = event.data;

    if (message?.type !== 'session-request') return;

    const refreshToken = getRefreshToken();
    const webId = getWebId();

    // Nessuna sessione da condividere: resto in silenzio.
    if (!refreshToken || !webId) return;

    const response: SessionResponse = {
      type: 'session-response',
      nonce: message.nonce,
      refreshToken,
      webId,
      apiHost: getApiHost(),
    };

    channel.postMessage(response);
  };
}

/**
 * Chiede alle altre tab una sessione condivisa. Risolve col payload della prima tab
 * che risponde entro {@link timeoutMs}, oppure null se nessuno risponde (nessuna tab
 * autenticata aperta o BroadcastChannel non supportato).
 */
export function requestSessionFromOtherTabs(timeoutMs = 500): Promise<SessionPayload | null> {
  return new Promise((resolve) => {
    if (typeof BroadcastChannel === 'undefined') {
      resolve(null);
      return;
    }

    const channel = new BroadcastChannel(CHANNEL_NAME);
    const nonce = `${Date.now()}-${Math.random()}`;
    let settled = false;

    const finish = (payload: SessionPayload | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      channel.close();
      resolve(payload);
    };

    channel.onmessage = (event: MessageEvent<ChannelMessage>) => {
      const message = event.data;

      if (message?.type === 'session-response' && message.nonce === nonce) {
        finish({
          refreshToken: message.refreshToken,
          webId: message.webId,
          apiHost: message.apiHost,
        });
      }
    };

    const timer = setTimeout(() => finish(null), timeoutMs);

    const request: SessionRequest = { type: 'session-request', nonce };
    channel.postMessage(request);
  });
}
